import { useState } from "react";
import "./newSignUp.css";
import { Link } from "react-router-dom";
import { GLOBAL_STYLES } from "../../../appConstants";
import Popup from "../../../ReusableFunctions/Popup";
import Select from "react-select";
import Check from "../../../assets/CheckCircle.svg";
import {
    getCountryCallingCode,
    getCountries,
    isValidPhoneNumber,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import ReactCountryFlag from "react-country-flag";
import { checkDspAccountExists, sendVerificationOTPOnSignUp } from "../../../apis/verification";
import showPasswordIcon from '../../../assets/Eye.svg';
import hidePasswordIcon from "../../../assets/EyeSlash.svg";
import { useSignUpContext } from "../../../context/SignUpContext";
import { setLogin, signUp } from "../../../apis/authentication";
import { setUser } from "../../../reducers/userReducer";
import { useDispatch } from "react-redux";

const customStyles = {
    control: (provided) => ({
        ...provided,
        minWidth: "100px",
        maxWidth: "150px",
        height: "35px",
        paddingBottom: "8px",
        fontSize: "14px",
        border: "none",
        boxShadow: "none",
    }),
    menu: (provided) => ({
        ...provided,
        maxHeight: "150px",
        overflowY: "auto",
    }),
    option: (provided, state) => ({
        ...provided,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 12px",
    }),
    singleValue: (provided) => ({
        ...provided,
        display: "flex",
        alignItems: "center",
        gap: "8px",
    }),
};

export default function BasicInfoStep() {
    const dispatch = useDispatch();
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const { currentStep, setCurrentStep, signUpDetails, setSignUpDetails } = useSignUpContext();
    const [errors, setErrors] = useState({
        first_name: "",
        last_name: "",
        mobile: "",
        email: "",
        password: "",
        confirm_password: "",
    });

    const countryOptions = getCountries().map((country) => ({
        value: country,
        label: (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <ReactCountryFlag
                    countryCode={country}
                    svg
                    style={{
                        width: "1.2em",
                        height: "1.2em",
                    }}
                    title={country}
                />
                <span>{`+${getCountryCallingCode(country)}`}</span>
            </div>
        ),
        countryCode: country, // preserve the country code
    }));

    const [selectedCountryOption, setSelectedCountryOption] = useState(() => {
        if (signUpDetails.countryCode) {
            const codeWithoutPlus = signUpDetails.countryCode.replace("+", "");
            const countryOption = countryOptions.find(
                (opt) => getCountryCallingCode(opt.value) === codeWithoutPlus
            );
            if (countryOption) return countryOption;
        }
        // Default to India if no match or no countryCode
        return countryOptions.find((opt) => opt.value === "IN");
    });

    const [countryCode, setCountryCode] = useState(
        signUpDetails.countryCode || `+${getCountryCallingCode("IN")}`
    );

    const [otpType, setOtpType] = useState(null);
    const [otpValue, setOtpValue] = useState("");
    const [openOtpModal, setOpenOtpModal] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleInputChange = (name, value) => {
        validateField(name, value);
        setSignUpDetails((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (name === "mobile" || name === "email") {
            let key = name === "mobile" ? "isMobileVerified" : "isEmailVerified"
            setSignUpDetails((prev) => ({ ...prev, [key]: false }));
        }
    };

    const validateAllFields = () => {
        let isValid = true;
        const newErrors = { ...errors };

        // Validate each field
        isValid = validateField("first_name", signUpDetails.first_name) && isValid;
        isValid = validateField("last_name", signUpDetails.last_name) && isValid;
        isValid = validateField("mobile", signUpDetails.mobile) && isValid;
        isValid = validateField("email", signUpDetails.email) && isValid;
        isValid = validateField("password", signUpDetails.password) && isValid;
        isValid =
            validateField("confirm_password", signUpDetails.confirm_password) && isValid;

        return isValid;
    };

    const isFormValid = () => {
        const allFieldsFilled = Object.values(signUpDetails).every(
            (value) => String(value)?.trim() !== ""
        );
        const noErrors = Object.values(errors).every((error) => !error);
        const bothVerified = signUpDetails.isEmailVerified && signUpDetails.isMobileVerified;

        const allFieldsValidated =
            signUpDetails.first_name.trim() !== "" &&
            signUpDetails.last_name.trim() !== "" &&
            signUpDetails.mobile.trim() !== "" &&
            signUpDetails.email.trim() !== "" &&
            signUpDetails.password.trim() !== "" &&
            signUpDetails.confirm_password.trim() !== "";

        return allFieldsFilled && noErrors && allFieldsValidated;
    };

    const handleCountryChange = (option) => {
        setSelectedCountryOption(option);
        const newCode = `+${getCountryCallingCode(option.value)}`;
        setSignUpDetails((prev) => ({
            ...prev,
            countryCode: newCode
        }));
        validateField("mobile", signUpDetails.mobile, newCode);
    };

    const handlePhoneChange = (e) => {
        const localNumber = e.target.value.replace(/\D/g, "");
        handleInputChange("mobile", localNumber);
    };

    const nameRegex = /^[A-Za-z]+$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W).{8,30}$/;

    const validateField = (name, value, customCountryCode) => {
        let error = "";
        switch (name) {
            case "first_name":
                if (!value.trim()) error = "First name is required";
                else if (value.length < 3) error = "should be at least 3 characters";
                else if (value.length > 20) error = "should be at most 20 characters";
                else if (!nameRegex.test(value))
                    error = "contain letters only (no spaces)";
                break;
            case "last_name":
                if (!value.trim()) error = "Last name is required";
                else if (value.length < 3) error = "should be at least 3 characters";
                else if (value.length > 20) error = "should be at most 20 characters";
                else if (!nameRegex.test(value))
                    error = "contain letters only (no spaces)";
                break;
            case "mobile":
                if (!value) {
                    error = "Mobile number is required";
                } else {
                    const selectedCountry = selectedCountryOption.value;
                    const testFull = `${customCountryCode || signUpDetails.countryCode}${value}`;
                    if (!isValidPhoneNumber(testFull)) {
                        error = "Invalid mobile number for selected country";
                    } else if (selectedCountry === "IN" && value.length !== 10) {
                        error = "mobile number must be exactly 10 digits";
                    }
                }
                break;
            case "email":
                if (!value.trim()) error = "Email is required";
                else if (!emailRegex.test(value)) error = "Enter a valid email address";
                break;
            case "password":
                if (!value.trim()) error = "Password is required";
                else if (value.length < 8)
                    error = "Password must be at least 8 characters";
                else if (value.length > 30)
                    error = "Password must be at most 30 characters";
                else if (!passwordRegex.test(value))
                    error =
                        "Password must include uppercase, lowercase, number & special character";
                break;
            case "confirm_password":
                if (!value.trim()) error = "Confirm password is required";
                else if (value !== signUpDetails.password) error = "Passwords do not match";
                break;
            default:
                break;
        }

        setErrors((prev) => ({ ...prev, [name]: error }));
        return !error;
    };

    const handleSignUp = async () => {
        const isValid = validateAllFields();

        if (!isValid || !isFormValid() || loading) return;
        setLoading(true);
        try {
            const updatedUser = {
                ...signUpDetails,
                firstname: signUpDetails.first_name,
                lastname: signUpDetails.last_name,
                phoneNo: signUpDetails.mobile,
                country_code: signUpDetails?.countryCode ? signUpDetails?.countryCode?.split("+")[1] : undefined,
            };
            await checkDspAccountExists(signUpDetails?.email, signUpDetails?.mobile);
            const res = await signUp(updatedUser);
            if (res.user) {
                setLogin(res.user);
                dispatch(setUser(res.user))
                setSignUpDetails({
                    email: "",
                    isEmailVerified: false,
                    mobile: "",
                    countryCode: "+91",
                    isMobileVerified: false,
                    first_name: "",
                    last_name: "",
                    password: "",
                    redirectLogin: true,
                })
            }
            setCurrentStep((prev) => prev + 1);
        } catch (err) {
            console.error("API error:", err);
            Popup.alert("ERROR", err, 'ERROR')
        } finally {
            setLoading(false)
        }

    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            handleSignUp();
        }
    };

    const mobileVerified =
        signUpDetails?.countryCode === '+91' ?
            signUpDetails?.mobile && signUpDetails?.isMobileVerified
            : signUpDetails?.mobile


    const emailVerified = signUpDetails?.email && signUpDetails?.isEmailVerified;

    return (
        <div>
            <div className="signup-form">
                <div className="signup-form-heading">
                    <p className="signUpform-title">Sign up a 15 days free trial</p>
                    <p className="form-subtitle">Please sign up with your details</p>
                </div>
                <div className="flex justify-between">
                    <div className="form-input-field" style={{ width: "47%" }}>
                        <input
                            type="text"
                            id="first_name"
                            name="first_name"
                            placeholder="First name"
                            value={signUpDetails.first_name}
                            onChange={(e) =>
                                handleInputChange("first_name", e.target.value)
                            }
                            onKeyPress={handleKeyPress}
                            required
                        />
                        <div className="error-text">{errors.first_name}</div>
                    </div>
                    <div className="form-input-field" style={{ width: "47%" }}>
                        <input
                            type="text"
                            id="last_name"
                            name="last_name"
                            placeholder="Last name"
                            value={signUpDetails.last_name}
                            onChange={(e) =>
                                handleInputChange("last_name", e.target.value)
                            }
                            onKeyPress={handleKeyPress}
                            required
                        />
                        <div className="error-text">{errors.last_name}</div>
                    </div>
                </div>

                <div className="form-input-field ">
                    <div className="flex flex-col gap-[0.2rem] ">
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                border: errors.phone
                                    ? "1px solid #D92D20"
                                    : "1px solid #D0D5DD",
                                borderRadius: "4px",
                                width: "100%",
                                height: "40px",
                                position: "relative",
                            }}
                        >
                            {/* react-select country code */}
                            <Select
                                value={selectedCountryOption}
                                onChange={handleCountryChange}
                                isDisabled={mobileVerified}
                                options={countryOptions}
                                isSearchable
                                styles={customStyles}
                                formatOptionLabel={(option) => option.label}
                                getOptionLabel={(option) =>
                                    `+${getCountryCallingCode(option.value)}`
                                }
                            />
                            <div
                                className="input-with-verify"
                                style={{
                                    width: "1px",
                                    height: "60%",
                                    backgroundColor: "#D0D5DD",
                                }}
                            />
                            <input
                                type="text"
                                placeholder="Mobile Number"
                                value={signUpDetails.mobile}
                                disabled={mobileVerified}
                                onKeyPress={handleKeyPress}
                                onChange={handlePhoneChange}
                                style={{
                                    flex: 1,
                                    border: "none",
                                    outline: "none",
                                    padding: "0 12px",
                                    height: "100%",
                                    paddingRight: "50px",
                                    fontSize: "14px",
                                    position: "relative",
                                }}
                            />
                            {mobileVerified && <div className="absolute right-3 top-2">
                                <img
                                    width={18}
                                    height={18}
                                    src={Check}
                                    alt="verified icon" />
                            </div>}
                        </div>
                    </div>
                    <div className="error-text">{errors.mobile}</div>
                </div>

                <div className="form-input-field">
                    <div className="input-with-verify">
                        <input
                            type="text"
                            id="email"
                            name="email"
                            disabled={emailVerified}
                            placeholder="Email Id"
                            value={signUpDetails.email}
                            onChange={(e) => handleInputChange("email", e.target.value)}
                            onKeyPress={handleKeyPress}
                            required
                        />
                        {emailVerified && <div className="absolute right-3 top-2">
                            <img
                                width={18}
                                height={18}
                                src={Check}
                                alt="verified icon" />
                        </div>}
                    </div>

                    <div className="error-text">{errors.email}</div>
                </div>

                <div className="form-input-field">
                    <div className="relative w-full">
                        <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            name="password"
                            placeholder="Password"
                            value={signUpDetails.password}
                            onChange={(e) =>
                                handleInputChange("password", e.target.value)
                            }
                            onKeyPress={handleKeyPress}
                            required
                        />
                        <div className="absolute right-3 top-2">
                            <img onClick={() => {
                                setShowPassword(!showPassword);
                            }}
                                className="cursor-pointer"
                                width={18}
                                height={18}
                                src={showPassword ? hidePasswordIcon : showPasswordIcon}
                                alt="password-show-hide-icon" />
                        </div>
                    </div>
                    <div className="error-text">{errors.password}</div>
                </div>
                <div className="form-input-field">
                    <div className="relative w-full">
                        <input
                            type={showConfirmPassword ? "text" : "password"}
                            id="confirm_password"
                            name="confirm_password"
                            placeholder="Confirm password"
                            value={signUpDetails.confirm_password}
                            onChange={(e) =>
                                handleInputChange("confirm_password", e.target.value)
                            }
                            onKeyPress={handleKeyPress}
                            required
                        />
                        <div className="absolute right-3 top-2">
                            <img onClick={() => {
                                setShowConfirmPassword(!showConfirmPassword);
                            }}
                                className="cursor-pointer"
                                width={18}
                                height={18}
                                src={showConfirmPassword ? hidePasswordIcon : showPasswordIcon}
                                alt="password-show-hide-icon" />
                        </div>
                    </div>
                    <div className="error-text">{errors.confirm_password}</div>
                </div>

                <div style={{ marginTop: "10px", marginBottom: "8px" }}>
                    <button
                        disabled={!isFormValid() || loading}
                        onClick={handleSignUp}
                        className={`w-full text-white rounded-[8px] text-[16px] font-[500] font-manrope h-[40px] text-center ${!isFormValid() || loading
                            ? "bg-[#0F5EFF] opacity-60 cursor-not-allowed"
                            : "bg-[#0F5EFF] cursor-pointer"
                            }`}
                    >
                        Continue
                    </button>
                </div>
                <div className="signUp-footer">
                    <p className="footer-text-header">
                        By continuing, you agree to our{" "}
                        <a href="https://bhumeet.app/terms-and-conditions/">Terms</a>{" "}
                        and{" "}
                        <a href="https://bhumeet.app/privacy-policy/">
                            Privacy Policy
                        </a>
                    </p>
                    <span className="footer-text-subheader">
                        Already have an account ?{" "}
                        <Link
                            to={"/login"}
                            style={{
                                color: GLOBAL_STYLES.BG_ACTIVE,
                                textDecoration: "none",
                                fontWeight: 700,
                            }}
                        >
                            Log in
                        </Link>
                    </span>
                </div>
            </div>
        </div>
    )
}
